/**
 * React Query hooks for Media Items
 *
 * Provides data fetching, caching, and mutations for media items.
 * Works with the SSE streaming endpoint - the query function streams all items
 * and returns them as a single array when complete.
 */

import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { mediaService, MediaStreamOptions } from "@/core/services/mediaService";
import { mediaKeys, queryClient } from "@/core/queryClient";
import { MediaStage, PostStatus } from "@/core/types/mediaTypes";
import { MediaContentType } from "@/core/types/mediaContentType";

const DEFAULT_PAGE_SIZE = 50;

export interface MediaQueryOptions {
  stageFilter?: MediaStage[];
  excludePostStatuses?: PostStatus[];
  contentTypes?: MediaContentType[];
  pageSize?: number;
  onlyNonLeadCarousel?: boolean;
  websiteTags?: string[];
}

interface MediaItems {
  images: MediaItem[];
  shortVideos: MediaItem[];
  longVideos: MediaItem[];
  videos: MediaItem[];
  postVideos: MediaItem[];
  audio: MediaItem[];
  all: MediaItem[];
}

interface MediaPage {
  items: MediaItem[];
  hasMore: boolean;
  nextOffset: number;
}

/**
 * Categorize media items by type
 */
const categorizeItems = (items: MediaItem[]): MediaItems => ({
  images: items.filter((i) => i.isImage()),
  shortVideos: items.filter((i) => i.isShortVideo()),
  longVideos: items.filter((i) => i.isLongVideo()),
  postVideos: items.filter((i) => i.isPostVideo()),
  videos: items.filter((i) => i.isGeneralVideo() && !i.isPostVideo()),
  audio: items.filter((i) => i.isAudio()),
  all: items,
});

/**
 * Fetch a page of media items for a client
 * Streams from multiple content type endpoints and combines results
 */
async function fetchMediaPage(
  clientId: string,
  options: MediaQueryOptions,
  offset: number = 0
): Promise<MediaPage> {
  const contentTypes: MediaContentType[] = options.contentTypes || ["photos", "stories", "reels", "videos", "post_video", "audio"];
  const pageSize = options.pageSize || DEFAULT_PAGE_SIZE;
  const allItems: MediaItem[] = [];
  let hasMore = false;

  for (const contentType of contentTypes) {
    let contentTypeHasMore = false;

    const items = await mediaService.streamMediaItems(
      clientId,
      {
        contentType,
        stageFilter: options.stageFilter,
        excludePostStatuses: options.excludePostStatuses,
        offset,
        limit: pageSize,
        onlyNonLeadCarousel: options.onlyNonLeadCarousel,
        websiteTags: options.websiteTags,
      },
      {
        onTotal: (totalData) => {
          if (typeof totalData === 'object' && totalData.hasMore) {
            contentTypeHasMore = true;
          }
        },
      }
    );
    allItems.push(...items);

    if (contentTypeHasMore) {
      hasMore = true;
    }
  }

  return {
    items: allItems,
    hasMore,
    nextOffset: offset + pageSize,
  };
}

/**
 * Fetch all media items for a client (no pagination)
 * Used for simpler queries that don't need pagination
 */
async function fetchAllMedia(
  clientId: string,
  options: MediaQueryOptions
): Promise<MediaItem[]> {
  const contentTypes: MediaContentType[] = options.contentTypes || ["photos", "stories", "reels", "videos", "post_video", "audio"];
  const allItems: MediaItem[] = [];

  for (const contentType of contentTypes) {
    const items = await mediaService.streamMediaItems(
      clientId,
      {
        contentType,
        stageFilter: options.stageFilter,
        excludePostStatuses: options.excludePostStatuses,
      },
      {
        onMediaItem: (item) => {
          // Items are collected by the service, we just need the final array
        },
      }
    );
    allItems.push(...items);
  }

  return allItems;
}

/**
 * Main hook for fetching media items with pagination
 * Returns categorized media items with pagination controls
 */
export function useMediaQuery(
  clientId: string | undefined,
  options: MediaQueryOptions = {}
) {
  const queryKey = clientId
    ? mediaKeys.filtered(clientId, {
        stageFilter: options.stageFilter,
        excludePostStatuses: options.excludePostStatuses,
        contentTypes: options.contentTypes,
        onlyNonLeadCarousel: options.onlyNonLeadCarousel,
        websiteTags: options.websiteTags,
      })
    : ["media", "none"];

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      if (!clientId) return { items: [], hasMore: false, nextOffset: 0 };
      return fetchMediaPage(clientId, options, pageParam);
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextOffset : undefined,
    initialPageParam: 0,
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });

  // Flatten all pages into categorized items
  const allItems = infiniteQuery.data?.pages.flatMap(page => page.items) || [];
  const categorized = categorizeItems(allItems);

  // Check if any page has more data
  const hasMore = infiniteQuery.data?.pages[infiniteQuery.data.pages.length - 1]?.hasMore ?? false;

  return {
    ...infiniteQuery,
    data: categorized,
    hasMore,
    loadMore: infiniteQuery.fetchNextPage,
    isLoadingMore: infiniteQuery.isFetchingNextPage,
  };
}

/**
 * Hook for fetching a single media item
 */
export function useSingleMediaQuery(
  clientId: string | undefined,
  mediaId: number | undefined
) {
  return useQuery({
    queryKey: clientId && mediaId ? mediaKeys.single(clientId, mediaId) : ["media", "none"],
    queryFn: async () => {
      // For single items, we look in the cache first
      // If not found, we'd need a dedicated endpoint (not currently available)
      // For now, return null and let the list query provide the data
      return null as MediaItem | null;
    },
    enabled: false, // Disabled by default - use list query
  });
}

/**
 * Type for infinite query data structure
 */
interface InfiniteMediaData {
  pages: MediaPage[];
  pageParams: number[];
}

// Debounce timers for upsert invalidation per client
const upsertTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Utility functions for SSE handlers to update the media cache
 * These are used by useRealtimeSync for granular updates
 */
export const mediaCacheUtils = {
  /**
   * Update a single media item in all relevant caches
   * Used when SSE notifies us that a media item was created or updated
   *
   * Debounces invalidation to prevent refetch loops when multiple SSE
   * events fire in quick succession (e.g. during media streaming).
   */
  upsertMediaItem(clientId: string, _mediaItem: MediaItem): void {
    const existing = upsertTimers.get(clientId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      upsertTimers.delete(clientId);
      queryClient.invalidateQueries({
        queryKey: mediaKeys.all(clientId),
        refetchType: 'active',
      });
    }, 500);

    upsertTimers.set(clientId, timer);
  },

  /**
   * Remove a media item from all caches
   * Used when SSE notifies us that a media item was deleted
   */
  removeMediaItem(clientId: string, mediaId: number): void {
    const queries = queryClient.getQueriesData<InfiniteMediaData>({
      queryKey: mediaKeys.all(clientId),
    });

    for (const [queryKey, data] of queries) {
      if (!data || !data.pages) continue;

      const updatedPages = data.pages.map(page => ({
        ...page,
        items: page.items.filter(item => item.id !== mediaId),
      }));
      queryClient.setQueryData<InfiniteMediaData>(queryKey, {
        ...data,
        pages: updatedPages,
      });
    }
  },

  /**
   * Invalidate all media queries for a client
   * Use for batch operations or when granular update isn't possible
   */
  invalidateAllMedia(clientId: string): void {
    queryClient.invalidateQueries({ queryKey: mediaKeys.all(clientId) });
  },

  /**
   * Invalidate media queries with specific filters
   */
  invalidateFilteredMedia(clientId: string, filters: Record<string, unknown>): void {
    queryClient.invalidateQueries({
      queryKey: mediaKeys.filtered(clientId, filters),
    });
  },
};
